import { Component } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { Vehicle } from 'src/app/core/interfaces/vehicle';
import { User } from 'src/app/core/interfaces/user';
import { Utils } from 'src/app/core/resources/utils';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { UserService } from 'src/app/core/services/user.service';
import { AuthService } from 'src/app/core/services/authentication.service';
import { VehiclesService } from '../../administration/vehicles/list-vehicles.service';
import { CargoSearchService } from './cargo-search.service';
import { CargoSearchAccessService } from './cargo-search-access.service';
import { ServiceMessages } from 'src/app/core/messages/service-messages.enum';
import { CargoStateEnum } from 'src/app/core/enums/cargoState.enum';

@Component({
  selector: 'app-cargo-search',
  templateUrl: './cargo-search.component.html',
  styleUrls: ['./cargo-search.component.scss']
})
export class CargoSearchComponent {
  public cargoId = new FormControl('', [Validators.required]);
  public cargoManifest = new FormControl('', [Validators.required]);
  public vehiclePlate = new FormControl('', [Validators.required, Validators.maxLength(8)]);
  public userDocument = new FormControl('', [Validators.required, Validators.maxLength(20)]);

  public searchingCargoId = false;
  public searchingManifest = false;
  public searchingVehicle = false;
  public searchingUser = false;

  constructor(
    private cargoSearchService: CargoSearchService,
    private cargoSearchAccessService: CargoSearchAccessService,
    private vehiclesService: VehiclesService,
    private userService: UserService,
    private snackBarService: SnackBarService,
    public utils: Utils,
    private router: Router,
  ) { }

  /**
   * @returns {number} cantidad de secciones visibles, usado para adaptar el layout
   */
  get visibleSectionsCount(): number {
    return [this.showCargoSection, this.showVehicleSection, this.showUserSection]
      .filter(Boolean).length;
  }

  searchCargoById(): void {
    if (this.utils.errorMessagesCustomized(this.cargoId, 'ID del servicio')) return;
    this.searchingCargoId = true;
    this.cargoSearchService.getCargoByConsecutive(this.cargoId.value).subscribe(
      (success: Cargo[]) => {
        this.searchingCargoId = false;
        if (!success || !success.length) this.snackBarService.openSnackBar("El servicio no existe", undefined, 'error');
        else this.navigateToCargo(success[0]);
      },
      () => {
        this.searchingCargoId = false;
        this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'alert');
      }
    );
  }

  searchCargoByManifest(): void {
    if (this.utils.errorMessagesCustomized(this.cargoManifest, 'manifiesto')) return;
    this.searchingManifest = true;
    this.cargoSearchService.getCargoByManifest(this.cargoManifest.value).subscribe(
      (success: Cargo[]) => {
        this.searchingManifest = false;
        if (!success || !success.length) this.snackBarService.openSnackBar("No existe un servicio con ese manifiesto", undefined, 'error');
        else this.navigateToCargo(success[0]);
      },
      () => {
        this.searchingManifest = false;
        this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'alert');
      }
    );
  }

  private navigateToCargo(cargo: Cargo): void {
    const base = cargo.state == CargoStateEnum.START_SERVICE
      ? '/cargo/tracking/'
      : cargo.state === CargoStateEnum.REQUEST
        ? '/service-request-form/menu-service/'
        : '/cargo/detail/';
    this.router.navigate([base + cargo.consecutive]);
  }

  searchVehicle(): void {
    if (this.utils.errorMessagesCustomized(this.vehiclePlate, 'placa', null, 8)) return;
    const plate = (this.vehiclePlate.value || '').toUpperCase().trim();
    this.searchingVehicle = true;
    this.vehiclesService.getVehicleFromOwnCompany(plate).subscribe(
      (vehicle: Vehicle) => {
        this.searchingVehicle = false;
        if (!vehicle || !vehicle.id) {
          this.snackBarService.openSnackBar("El vehículo no existe", undefined, 'error');
          return;
        }
        this.router.navigate(['/administration/vehicles/detail', vehicle.id]);
      },
      () => {
        this.searchingVehicle = false;
        this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'alert');
      }
    );
  }

  searchUser(): void {
    if (this.utils.errorMessagesCustomized(this.userDocument, 'cédula', null, 20)) return;
    const document = (this.userDocument.value || '').trim();
    this.searchingUser = true;
    this.userService.getUserByDocument(document).subscribe(
      (user: User) => {
        this.searchingUser = false;
        if (!user || !user.information || !user.information.document) {
          this.snackBarService.openSnackBar("El usuario no existe", undefined, 'error');
          return;
        }
        this.router.navigate(['/administration/admin-users/detail', user.information.document]);
      },
      () => {
        this.searchingUser = false;
        this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'alert');
      }
    );
  }

  get showCargoSection(): boolean {
    return this.cargoSearchAccessService.showCargoSection;
  }

  get showManifestSection(): boolean {
    return this.cargoSearchAccessService.showManifestSection;
  }

  get showVehicleSection(): boolean {
    return this.cargoSearchAccessService.showVehicleSection;
  }

  get showUserSection(): boolean {
    return this.cargoSearchAccessService.showUserSection;
  }
}
